import React from "react";
import {ColDef, CellClickedEvent, ICellRendererParams} from "@ag-grid-community/core";
import {
  DateTimeCellRenderer,
  getDate,
  getStopDateTimeCellValue,
  StopDateTimeType
} from "../../common/cell-renderers/DateTimeCellRenderer";
import DoubleFieldCellRenderer, {DoubleFieldCellValue} from "../../common/cell-renderers/DoubleFieldCellRenderer";
import {StopTypeIconCellRenderer} from "../../common/cell-renderers/StopTypeIconCellRenderer";
import ServiceTypeCellRenderer from "../../common/cell-renderers/ServiceTypeCellRenderer";
import {JobPiecesOrWeightCellRenderer} from "../../common/cell-renderers/JobPiecesAndWeightCellRenderer";
import {CellRendererWithToolTip} from "../../common/cell-renderers/CellRendererWithToolTip";
import {ContactCellRenderer, ContactValueGetter} from "../../common/cell-renderers/ContactCellRenderer";
import {
  ColoredJobNumberCellRenderer,
  ManifestJobCellRendererParams
} from "../../common/cell-renderers/ColoredJobNumberCellRenderer";
import {JobNumberTooltipUsage} from "../../common/cell-renderers/JobNumberCellRenderer";
import {DetailsCellRenderer} from "../../common/cell-renderers/DetailsCellRenderer";
import {ETACellRenderer} from "../../common/cell-renderers/ETACellRenderer";
import JobColHeaderRenderer from "../../manifest/details/components/JobColHeaderRenderer";
import {copyToClipboard, getStopFullAddressText} from "../../../utils/General";
import {orderSkillsValueGetter} from "../../../utils/ag-grid/AGGridUtils";
import {OrderRequirement} from "../../../generated/graphql";
import {AgStop} from "../../common/types";
import DragCellRenderer from "../../manifest/details/components/DragCellRenderer";
import StopActionMenuCellRenderer from "../components/StopActionMenuCellRenderer";
import StopStatusRenderer from "../../manifest/details/StopStatusRenderer";
import {RowData} from "../types/types";
import {isCompleted} from "../../../utils/stops/StopUtils";

export const buildStopsColumnDefs = (params: {
  appState: {isTimeFormat24hr: boolean};
  isTimezoneOfJobStop: boolean;
  etaFromOptimizationEnabled: boolean | undefined;
  skillsEnabled: boolean | undefined;
  skills: any[];
  buildJobColoringIndex: (stops: AgStop[]) => Map<string, number>;
  workingRowDataRef?: React.MutableRefObject<RowData[]>;
  onMoveJobClick?: (stop: AgStop, parentDriverId: number, parentRouteId: string) => void;
  onDeleteJob?: (jobId: number, driverRowIndex: number) => void;
  parentDriverId?: number;
  parentRouteId?: string;
}): ColDef<AgStop>[] => {
  const {
    appState,
    isTimezoneOfJobStop,
    etaFromOptimizationEnabled,
    skillsEnabled,
    skills,
    buildJobColoringIndex,
    workingRowDataRef,
    onMoveJobClick,
    onDeleteJob,
    parentDriverId,
    parentRouteId
  } = params;
  return [
    {
      colId: "dragColumn",
      headerName: "",
      pinned: "left",
      lockPosition: "left",
      lockPinned: true,
      suppressMovable: true,
      suppressColumnsToolPanel: true,
      sortable: false,
      resizable: false,
      width: 28,
      minWidth: 28,
      maxWidth: 28,
      // DP-3175 B08: returns false for completed stops — no drag handle, no drag.
      rowDrag: (p) => !p.data || !isCompleted(p.data),
      cellRenderer: DragCellRenderer,
      cellRendererParams: (p: ICellRendererParams<AgStop>) => ({
        isCompleted: p.data ? isCompleted(p.data) : false,
        isAllowResequence: true,
        content: ""
      })
    },
    ...(onMoveJobClick && onDeleteJob && workingRowDataRef && parentDriverId != null && parentRouteId != null
      ? [
          {
            colId: "actionsColumn",
            headerName: "",
            pinned: "left" as const,
            lockPosition: "left" as const,
            lockPinned: true,
            suppressMovable: true,
            suppressColumnsToolPanel: true,
            sortable: false,
            resizable: false,
            width: 20,
            minWidth: 20,
            maxWidth: 20,
            cellRenderer: StopActionMenuCellRenderer,
            cellRendererParams: {
              workingRowDataRef,
              onMoveJobClick,
              onDeleteJob,
              parentDriverId,
              parentRouteId
            }
          } as ColDef<AgStop>
        ]
      : []),
    {
      field: "manifestSequence",
      headerName: "#",
      initialWidth: 20,
      resizable: false,
      pinned: "left",
      lockPosition: "left",
      lockPinned: true
    },
    {
      field: "job.jobNumber",
      headerName: "Job #",
      initialWidth: 40,
      pinned: "left",
      lockPosition: "left",
      lockPinned: true,
      headerComponent: JobColHeaderRenderer,
      headerComponentParams: (p: ICellRendererParams<AgStop>) => {
        const stops: AgStop[] = [];
        p.api.forEachNode((node) => {
          if (node.data) stops.push(node.data);
        });
        return {
          jobColoringIndex: buildJobColoringIndex(stops),
          stopLength: stops.length
        };
      },
      cellRenderer: ColoredJobNumberCellRenderer,
      cellRendererParams: (p: ICellRendererParams<AgStop>) => {
        const stops: AgStop[] = [];
        p.api.forEachNode((node) => {
          if (node.data) stops.push(node.data);
        });
        return {
          jobColoringIndex: buildJobColoringIndex(stops),
          usage: JobNumberTooltipUsage.MANIFEST_DETAILS_GRID,
          dispatchNotesEnabled: false,
          stops: stops
        } as ManifestJobCellRendererParams;
      }
    },
    {
      field: "stopType",
      headerName: "Type",
      initialWidth: 40,
      cellRenderer: StopTypeIconCellRenderer
    },
    {
      field: "scheduledDateTime",
      headerName: "Scheduled Time",
      initialWidth: 120,
      cellRenderer: DateTimeCellRenderer,
      valueGetter: (p) => {
        return getStopDateTimeCellValue({
          data: p.data as AgStop,
          is24HourFormat: appState.isTimeFormat24hr,
          isTimezoneOfJobStop,
          typeOfDateTime: StopDateTimeType.SCHEDULED_TIME
        });
      }
    },
    ...((etaFromOptimizationEnabled
      ? [
          {
            field: "eta",
            headerName: "ETA",
            initialWidth: 100,
            pinned: "left",
            lockPosition: "left",
            lockPinned: true,
            cellRenderer: ETACellRenderer,
            cellRendererParams: {
              style: {
                gap: "0"
              }
            },
            valueGetter: (p: any) => {
              return getStopDateTimeCellValue({
                data: p.data as AgStop,
                is24HourFormat: appState.isTimeFormat24hr,
                isTimezoneOfJobStop,
                showPreposition: false,
                typeOfDateTime: StopDateTimeType.ETA
              });
            }
          }
        ]
      : []) as ColDef<AgStop>[]),
    {
      field: "address",
      headerName: "Address",
      cellRenderer: DoubleFieldCellRenderer,
      valueGetter: (p) => {
        if (!p.data) return undefined;
        return {
          upperField: {
            value: p.data.address,
            tooltip: p.data.address
          },
          lowerField: {
            value: `${p.data.city}, ${p.data.state}, ${p.data.zip}`,
            tooltip: `${p.data.city}, ${p.data.state}, ${p.data.zip}`,
            style: {
              color: "#6B7280"
            }
          }
        } as DoubleFieldCellValue;
      },
      cellStyle: {
        cursor: "pointer"
      },
      onCellClicked: (p: CellClickedEvent<AgStop>) => {
        if (!p.data) return;
        const address = getStopFullAddressText(p.data);
        copyToClipboard(address).then(() => {
          /*Noop*/
        });
      },
      initialWidth: 109
    },
    {
      field: "dispatchZone",
      headerName: "Zone",
      initialWidth: 100
    },
    {
      colId: "order.service", // This is for matching with colId from manifest details grid
      headerName: "SVT",
      initialWidth: 100,
      cellRenderer: ServiceTypeCellRenderer,
      // DP-3175 B10: New jobs have service on the Job level; manifest stops have it on order.
      valueGetter: (p) => p.data?.job?.service ?? (p.data as any)?.order?.service ?? ""
    },
    {
      field: "job.pieces",
      headerName: "Pieces",
      initialWidth: 100,
      cellRenderer: JobPiecesOrWeightCellRenderer
    },
    {
      field: "job.weight",
      headerName: "Weight",
      initialWidth: 100,
      cellRenderer: JobPiecesOrWeightCellRenderer
    },
    {
      field: "name",
      headerName: "Stop Name",
      initialWidth: 140,
      cellRenderer: CellRendererWithToolTip
    },
    {
      field: "order.customer.name",
      headerName: "Customer Name",
      initialWidth: 150,
      cellRenderer: CellRendererWithToolTip
    },
    {
      colId: "details",
      headerName: "Details",
      initialWidth: 70,
      cellRenderer: DetailsCellRenderer,
      cellStyle: {justifyContent: "center"}
    },
    {
      field: "see",
      headerName: "Contact",
      initialWidth: 90,
      cellRenderer: ContactCellRenderer,
      valueGetter: ContactValueGetter,
      initialHide: true
    },
    {
      field: "order.notes",
      headerName: "Order Notes",
      initialWidth: 90,
      cellRenderer: CellRendererWithToolTip,
      initialHide: true
    },
    {
      field: "note",
      headerName: "Stop Notes",
      initialWidth: 90,
      cellRenderer: CellRendererWithToolTip,
      initialHide: true
    },
    {
      field: "order.customer.notes",
      headerName: "Customer Notes",
      initialWidth: 90,
      cellRenderer: CellRendererWithToolTip,
      initialHide: true
    },
    {
      field: "order.customer.customerCode",
      headerName: "Account #",
      initialWidth: 90,
      cellRenderer: CellRendererWithToolTip,
      initialHide: true
    },
    {
      field: "order.declaredValue",
      headerName: "Declared Value",
      initialWidth: 90,
      initialHide: true
    },
    {
      field: "order.codAmount",
      headerName: "COD Amount",
      initialWidth: 150,
      initialHide: true
    },
    {
      field: "lateDateTime",
      headerName: "Late Time",
      initialWidth: 120,
      cellRenderer: DateTimeCellRenderer,
      valueGetter: (p) => {
        return getStopDateTimeCellValue({
          data: p.data as AgStop,
          is24HourFormat: appState.isTimeFormat24hr,
          isTimezoneOfJobStop,
          typeOfDateTime: StopDateTimeType.LATE_TIME
        });
      },
      initialHide: true
    },
    {
      field: "earlyDateTime",
      headerName: "Early Time",
      initialWidth: 120,
      cellRenderer: DateTimeCellRenderer,
      valueGetter: (p) => {
        return getStopDateTimeCellValue({
          data: p.data as AgStop,
          is24HourFormat: appState.isTimeFormat24hr,
          isTimezoneOfJobStop,
          typeOfDateTime: StopDateTimeType.EARLY_TIME
        });
      },
      initialHide: true
    },
    {
      field: "job.routeNumber",
      headerName: "Route Number",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip
    },
    {
      field: "job.site.code",
      headerName: "Site",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip
    },
    {
      field: "order.auth",
      headerName: "Auth",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip
    },
    {
      field: "order.alias",
      headerName: "Order Alias",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip
    },
    {
      // DP-3175 B10: New jobs have `job.jobAlias`, manifest stops have `job.alias`.
      headerName: "Job Alias",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip,
      valueGetter: (p) => (p.data?.job as any)?.jobAlias ?? p.data?.job?.alias ?? ""
    },
    {
      field: "order.caller",
      headerName: "Caller",
      initialWidth: 90,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip
    },
    {
      field: "order.priority.description",
      headerName: "Order Priority",
      initialHide: true,
      initialWidth: 120,
      cellRenderer: CellRendererWithToolTip
    },
    {
      colId: "order.createDate", // For matching with colId from manifest details grid
      headerName: "Created Date",
      initialWidth: 120,
      initialHide: true,
      cellRenderer: CellRendererWithToolTip,
      // DP-3175 B10: both NestedStopOrder and ManifestStopOrder use createDate, not createdDateTime
      valueGetter: (params: any) => {
        const createDate = params.data?.order?.createDate ?? params.data?.order?.createdDateTime;
        if (!createDate) return undefined;
        return getDate(createDate, params.data?.timeZone, isTimezoneOfJobStop, "MM/dd/yyyy zzz");
      }
    },
    {
      field: "order.user.userName",
      headerName: "User",
      initialWidth: 120,
      initialHide: true
    },
    {
      field: "order.requirements.name",
      headerName: "Order Requirements",
      valueGetter: (p: any) => p.data?.order?.requirements?.map((or: OrderRequirement) => or.name).join(", "),
      initialHide: true
    },
    {
      field: "jobStopStatus",
      headerName: "Status",
      initialWidth: 60,
      initialHide: true,
      cellRenderer: StopStatusRenderer
    },
    ...(skillsEnabled
      ? [
          {
            colId: "order.orderSkills.id",
            field: "order.orderSkills.id",
            headerName: "Skills",
            initialHide: true,
            valueGetter: (p: any) => {
              return orderSkillsValueGetter(p, skills);
            },
            cellRenderer: CellRendererWithToolTip
          }
        ]
      : [])
  ] as ColDef<AgStop>[];
};
